<template>
  <div class="bg-white rounded-lg mt-4 pb-5 px-6 mb-4">
    <div class="flex items-center gap-2 mb-6">
      <div class="w-3 h-3 bg-green-500 rounded-full"></div>
      <h2 class="text-xl font-semibold text-gray-900">
        Completed Sessions ({{ totalSessions ?? sessions.length }})
      </h2>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div
        v-for="session in sessions"
        :key="session.id"
        @click="openSessionDetails(session)"
        class="bg-white border-2 border-black rounded-lg p-4 sm:p-6 shadow-[4px_4px_0_0_rgba(0,0,0,1)] transition-all duration-300 hover:scale-102 cursor-pointer overflow-hidden"
      >
        <!-- Session Header -->
        <div class="mb-4">
          <!-- Desktop: Tutor Info + Status Tags in same row -->
          <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
            <!-- Tutor Info -->
            <div class="flex items-center gap-3">
              <!-- Use UserAvatar for active users -->
              <UserAvatar
                v-if="session.tutorName !== 'Deactivated User'"
                :src="session.tutorProfileImage"
                :name="session.tutorName"
                size="md"
              />
              <!-- Show UserCircleIcon only for deactivated users -->
              <div
                v-else
                class="w-10 h-10 flex-shrink-0 bg-gray-300 rounded-full flex items-center justify-center"
              >
                <UserCircleIcon class="w-10 h-10 text-gray-500" />
              </div>
              <div class="flex-1">
                <h4 class="font-medium text-gray-900">{{ session.tutorName }}</h4>
              </div>
            </div>

            <!-- Status Tags - Desktop: upper right, Mobile: below -->
            <div class="flex items-center gap-2 flex-shrink-0">
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 border-2 border-blue-800 text-blue-800 whitespace-nowrap"
              >
                Completed
              </span>
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                :class="getTypeClasses(session.mode)"
              >
                {{ session.mode === "face-to-face" ? "Face-to-Face" : "Online" }}
              </span>
            </div>
          </div>
        </div>

        <!-- Session Details -->
        <div class="space-y-3 mb-4">
          <div class="flex items-center gap-2 ml-4  text-md text-gray-600">
            <svg
              class="w-5 h-5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            <span class="truncate" :title="session.subject">{{ session.subject }}</span>
          </div>

          <div
            class="flex justify-between border-2 border-blue-800 bg-blue-50/50 rounded-lg py-2 px-4"
          >
            <div class="flex items-center gap-2 text-sm text-blue-800">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span class="font-semibold">{{ session.date }}</span>
            </div>

            <div class="flex items-center gap-2 text-sm text-blue-800">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span class="font-semibold">{{ session.time }}</span>
            </div>
          </div>

          <div
            v-if="session.location"
            class="flex items-center gap-2 border border-gray-200 bg-gray-50/50 rounded-sm py-2 px-4 text-sm text-gray-600 min-w-0"
          >
            <svg
              class="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span class="truncate" :title="session.location">{{ session.location }}</span>
          </div>
        </div>

        <!-- Ratings (hidden for validators) -->
        <div v-if="!hideInteractionButtons" class="mb-4">
          <div class="text-sm mb-2">
            <span class="text-gray-600"
              >Your Rating:
              <span class="font-semibold text-green-700">
                {{
                  session.yourRating && session.yourRating !== "0"
                    ? session.yourRating + "/5.0"
                    : "No rate given yet"
                }}
              </span>
            </span>
          </div>
          <div class="text-sm">
            <span class="text-gray-600"
              >Tutor's Rating:
              <span class="font-semibold text-blue-700">
                {{
                  session.tutorRating && session.tutorRating !== "0"
                    ? session.tutorRating + "/5.0"
                    : "No rate given yet"
                }}
              </span>
            </span>
          </div>
        </div>

        <!-- Feedback Status (hidden for validators) -->
        <div
          v-if="!hideInteractionButtons && session.feedbackStatus === 'pending'"
          class="mb-4 p-3 rounded-md bg-amber-100 border-2 border-amber-500 pending-feedback"
        >
          <div class="text-sm flex items-center gap-2">
            <svg class="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clip-rule="evenodd"
              />
            </svg>
            <span class="font-semibold text-amber-800">Pending Feedback</span>
          </div>
        </div>
        <div
          v-else-if="!hideInteractionButtons && session.feedbackStatus === 'given'"
          class="mb-4 p-3 rounded-md bg-green-50 border border-green-200"
        >
          <div class="text-sm">
            <span class="font-medium text-green-600">Feedback Given</span>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="space-y-2 sm:space-y-3" @click.stop>
          <!-- Row: Give Feedback and Message (side by side) - hidden for validators -->
          <div v-if="!hideInteractionButtons" class="flex flex-col sm:flex-row gap-2">
            <button
              v-if="
                session.feedbackStatus !== 'given' ||
                !session.yourRating ||
                session.yourRating === '0'
              "
              @click="navigateToFeedback(session)"
              class="flex-1 px-3 py-2 bg-white border-2 border-orange-700 text-orange-800 rounded-md shadow-[4px_4px_0_0_rgba(194,65,12,1)] hover:bg-orange-500 hover:text-white transition-colors text-sm font-medium flex items-center justify-center gap-2 cursor-pointer min-w-0"
            >
              <StarIcon class="w-4 h-4 flex-shrink-0" />
              <span class="truncate">Give Feedback</span>
            </button>
            <span
              v-else
              class="flex-1 inline-flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-400 border-2 border-gray-300 cursor-not-allowed min-w-0"
            >
              <StarIcon class="w-4 h-4 mr-2 flex-shrink-0" />
              <span class="truncate">Feedback Given</span>
            </span>
            <button
              @click.stop="openMessagesView(session)"
              :disabled="isLoadingMessage === session.id"
              class="flex-1 px-3 py-2 bg-white border-2 border-blue-800 text-blue-800 rounded-md shadow-[4px_4px_0_0_rgba(30,64,175,1)] hover:bg-blue-500 hover:text-white transition-colors text-sm font-medium flex items-center justify-center gap-2 cursor-pointer min-w-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div v-if="isLoadingMessage === session.id" class="animate-spin rounded-full h-4 w-4 border-2 border-blue-800 border-t-transparent flex-shrink-0"></div>
              <svg
                v-else
                class="w-4 h-4 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              <span class="truncate">{{ isLoadingMessage === session.id ? 'Loading...' : 'Message' }}</span>
            </button>
          </div>

          <!-- Second row: Generate AI Summary or Take Post-Test or Take Pre-Test (full width) - always visible -->
          <button
            v-if="!showPostTestButton && !showPreTestButton"
            @click.stop="navigateToGenerateAI(session)"
            class="ai-generate-button w-full px-3 py-2 bg-white text-purple-700 rounded-md transition-all text-sm font-medium flex items-center justify-center gap-2 cursor-pointer border-2 border-purple-600 shadow-[4px_4px_0_0_rgba(126,34,206,1)] hover:bg-purple-600 hover:text-white hover:shadow-[2px_2px_0_0_rgba(126,34,206,1)] relative overflow-hidden"
          >
            <svg class="w-4 h-4 ai-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
            <span>Generate Learning Material with AI</span>
          </button>

          <!-- Post-Test Button for Validators -->
          <button
            v-else-if="showPostTestButton"
            @click.stop="handleTakePostTest(session)"
            class="ai-generate-button w-full px-3 py-2 bg-white text-orange-700 rounded-md transition-all text-sm font-medium flex items-center justify-center gap-2 cursor-pointer border-2 border-orange-600 shadow-[4px_4px_0_0_rgba(234,88,12,1)] hover:bg-orange-600 hover:text-white hover:shadow-[2px_2px_0_0_rgba(234,88,12,1)] relative overflow-hidden"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span>Take Post-Test</span>
          </button>

          <!-- Pre-Test Button for Validators -->
          <button
            v-else-if="showPreTestButton"
            @click.stop="handleTakePreTest(session)"
            class="ai-generate-button w-full px-3 py-2 bg-white text-blue-700 rounded-md transition-all text-sm font-medium flex items-center justify-center gap-2 cursor-pointer border-2 border-blue-600 shadow-[4px_4px_0_0_rgba(29,78,216,1)] hover:bg-blue-600 hover:text-white hover:shadow-[2px_2px_0_0_rgba(29,78,216,1)] relative overflow-hidden"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span>Take Pre-Test</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Session Details Modal -->
    <SessionDetailsModal
      :isOpen="isModalOpen"
      :session="selectedSession"
      @close="closeModal"
      @navigateToFeedback="navigateToFeedback"
      @openMessages="openMessagesView"
    />

    <!-- Session Toast Container -->
    <SessionToast />
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { StarIcon, UserCircleIcon } from "@heroicons/vue/24/outline";
import type { CompletedSession } from "@/types/session";
import { auth, getDoc, doc, db } from "@/firebase";
import { logInfo, logError, logSuccess } from "@/utils/secureLogger";
import UserAvatar from "@/components/UserAvatar.vue";
import SessionDetailsModal from "./SessionDetailsModal.vue";
import SessionToast from "./SessionToast.vue";

const router = useRouter();

interface Props {
  sessions: CompletedSession[];
  hideHeader?: boolean;
  hideInteractionButtons?: boolean; // Hide feedback, message, and rating elements
  showPostTestButton?: boolean; // Show "Take Post-Test" button instead of "Generate AI" button
  showPreTestButton?: boolean; // Show "Take Pre-Test" button instead of "Generate AI" button
  totalSessions?: number; // Total count for accurate counter (used when sessions are paginated)
}

const props = withDefaults(defineProps<Props>(), {
  hideHeader: false,
  hideInteractionButtons: false, // Default: show all buttons
  showPostTestButton: false, // Default: show Generate AI button
  showPreTestButton: false, // Default: show Generate AI button
  totalSessions: undefined,
});

const emit = defineEmits<{
  takePostTest: [session: CompletedSession];
  takePreTest: [session: CompletedSession];
}>();

// Modal state
const isModalOpen = ref(false);
const selectedSession = ref<CompletedSession | null>(null);

// Message loading state
const isLoadingMessage = ref<string | null>(null); // Store session ID being loaded

// Modal handlers
const openSessionDetails = (session: CompletedSession) => {
  selectedSession.value = session;
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  selectedSession.value = null;
};

const openMessagesView = async (session: CompletedSession) => {
  try {
    // Set loading state for this specific session
    isLoadingMessage.value = session.id;
    
    logInfo("Starting conversation from completed session", { sessionId: session.id });

    // Import the messaging permission service
    const { messagingPermissionService } = await import("@/services/messagingPermissionService");

    // Get current user
    const currentUser = auth.currentUser;
    if (!currentUser) {
      alert("Please log in to send messages");
      isLoadingMessage.value = null;
      return;
    }

    // Create conversation from session context
    const result = await messagingPermissionService.createConversationFromSession(
      session.id,
      currentUser.uid
    );

    if (!result.success) {
      alert(result.reason || "Unable to start conversation");
      isLoadingMessage.value = null;
      return;
    }

    // Get the tutor's information for CometChat
    const tutorId = session.tutorId || session.tutorName; // Use tutorId if available, fallback to tutorName
    const tutorName = session.tutorName;

    if (!tutorId) {
      alert("Unable to identify the tutor for messaging");
      return;
    }

    // Note: CometChat initialization and user creation will be handled by the messaging view
    // We just create the conversation and navigate

    // Navigate to student messages view with the specific conversation
    router.push({
      path: "/student-messages",
      query: {
        conversation: result.conversationId,
        contact: tutorId,
      },
    });

    logSuccess("Successfully navigated to messaging with tutor", {
      tutorId,
      conversationId: result.conversationId,
    });
    
    // Clear loading state after successful navigation
    isLoadingMessage.value = null;
    
  } catch (error) {
    logError("Error starting conversation with tutor", error);
    alert("Failed to start conversation. Please try again.");
    isLoadingMessage.value = null;
  }
};

const navigateToFeedback = (session: CompletedSession) => {
  // Navigate to feedback route with session ID as parameter
  router.push(`/feedback/${session.id}`);
};

const navigateToGenerateAI = async (session: CompletedSession) => {
  // Check if current user is a validator
  const currentUser = auth.currentUser;
  let isValidator = false;

  if (currentUser) {
    try {
      const userDoc = await getDoc(doc(db, "users", currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        isValidator = userData.userType === "validator";
        console.log("🔍 User userType detected:", userData.userType, "isValidator:", isValidator);
      }
    } catch (error) {
      console.error("Error checking user role:", error);
    }
  }

  // Navigate to appropriate route based on user role
  const basePath = isValidator ? "/validator/ai-testing" : "/student/study-materials";

  console.log("🚀 Navigating to:", basePath, "with query:", {
    sessionId: session.id,
    subject: session.subject,
    generateAI: "true",
  });

  router.push({
    path: basePath,
    query: {
      sessionId: session.id,
      subject: session.subject,
      generateAI: "true", // Flag to show AI generator
    },
  });
};

const handleTakePostTest = (session: CompletedSession) => {
  emit("takePostTest", session);
};

const handleTakePreTest = (session: CompletedSession) => {
  emit("takePreTest", session);
};

const getTypeClasses = (mode: string) => {
  switch (mode) {
    case "face-to-face":
      return "bg-orange-100 text-orange-800 border-2 border-orange-800";
    case "online":
      return "bg-blue-100 text-blue-800 border-2 border-blue-800";
    default:
      return "bg-gray-100 text-gray-800 border-2 border-gray-800";
  }
};

const getFeedbackClasses = (status: string) => {
  switch (status) {
    case "given":
      return "bg-blue-50 border border-blue-200 ";
    case "pending":
      return "bg-orange-50 border border-orange-200";
    default:
      return "bg-gray-50 border border-gray-200";
  }
};
</script>

<style scoped>
/* Pending Feedback Pulse Animation */
@keyframes pulse-border {
  0%,
  100% {
    border-color: rgb(245 158 11);
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4);
  }
  50% {
    border-color: rgb(217 119 6);
    box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.1);
  }
}

@keyframes gentle-glow {
  0%,
  100% {
    background-color: rgb(254 243 199);
  }
  50% {
    background-color: rgb(253 230 138);
  }
}

.pending-feedback {
  animation: pulse-border 2s ease-in-out infinite, gentle-glow 2s ease-in-out infinite;
}

/* AI Button Animations */
@keyframes ai-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

@keyframes ai-icon-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.ai-generate-button {
  animation: ai-pulse 3s ease-in-out infinite;
}

.ai-generate-button:hover {
  animation: ai-pulse 1.5s ease-in-out infinite;
}

.ai-generate-button:hover .ai-icon {
  animation: ai-icon-spin 2s linear infinite;
}

.ai-generate-button::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(126, 34, 206, 0.2), transparent);
  transition: left 0.6s;
}

.ai-generate-button:hover::before {
  left: 100%;
}
</style>
